/**
 * Wire schemas for The Surface — the Operations tab that holds the OAS
 * (ADR-0119). The payload has two parts: the rule-surfaced attention **belt**,
 * grouped into ordered `BeltBlock`s, and the operator's private **pinned**
 * watches. The page renders three tabs — By status (the belt), By firm (the
 * same rows re-pivoted client-side), Pinned. To-dos live on the Dashboard.
 *
 * A pin carries the matter's **current** book card (live provenance), so the
 * card's shape tells you where the matter lives.
 *
 * Spec: docs/ops-attention-system/ (evicted 2026-08-29, #775 round 10) · ADR-0119
 */

import { z } from "zod";
import { LeadSchema } from "./lead.js";
import { UnderReviewLeadSchema, UnderReviewMiscSchema } from "./under-review.js";
import { AcceptedLeadSchema } from "./accepted.js";
import { PlacedCaseSchema } from "./placed.js";
import { SettledCaseSchema } from "./settled.js";
import { DroppedCaseSchema } from "./dropped.js";
import { ClosedCaseSchema } from "./closed.js";
import { RejectedCaseSchema } from "./rejected.js";
import { IntakeLeadSchema } from "./intake-verification.js";
import { TaskSchema } from "./task.js";
import { DashboardRowSchema } from "./dashboard.js";

/**
 * The matter's current book card — its live provenance. The `kind` discriminant
 * selects the renderer; the payload is that book's existing card type, so The
 * Surface reuses the book's component verbatim. `lead` is the fallback: a matter
 * the aggregator couldn't place in any book (off-path data, or a rejected matter
 * older than the recent window) renders the minimal matter card so it's never
 * invisible.
 */
export const SurfaceCardSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("intake"), card: IntakeLeadSchema }),
  z.object({ kind: z.literal("under-review-lead"), card: UnderReviewLeadSchema }),
  z.object({ kind: z.literal("under-review-misc"), card: UnderReviewMiscSchema }),
  z.object({ kind: z.literal("accepted"), card: AcceptedLeadSchema }),
  z.object({ kind: z.literal("placed"), card: PlacedCaseSchema }),
  z.object({ kind: z.literal("settled"), card: SettledCaseSchema }),
  z.object({ kind: z.literal("dropped"), card: DroppedCaseSchema }),
  z.object({ kind: z.literal("closed"), card: ClosedCaseSchema }),
  z.object({ kind: z.literal("rejected"), card: RejectedCaseSchema }),
  z.object({ kind: z.literal("lead"), card: LeadSchema }),
]);
export type SurfaceCard = z.infer<typeof SurfaceCardSchema>;

/**
 * One pin on The Surface — a private "watch" (a Task with no assignee, visible
 * only to its creator) plus the matter's current book card for the light row +
 * detail panel (ADR-0119). To-dos live on the Dashboard.
 */
export const SurfacePinSchema = z.object({
  task: TaskSchema,
  card: SurfaceCardSchema,
});
export type SurfacePin = z.infer<typeof SurfacePinSchema>;

/**
 * One **block** on the attention belt (OAS rebuild, build-plan Part 3). A block
 * is a category of attention — produced by one belt producer — carrying its rows
 * worst-first. The Surface renders the non-empty blocks as stacked, labeled
 * sections in `order`; clearing a block's rows makes its section vanish and the
 * next becomes the top (the "work one block → it completes → next opens" feel).
 *
 *   - `id`    — stable producer id (e.g. "ur-no-ack"); the section's React key.
 *   - `label` — the section heading shown to the operator.
 *   - `order` — fixed block order (Chris's canonical 1–17); blocks sort by it.
 *   - `rows`  — the matching rows, already sorted worst-first. Never empty (the
 *               composer drops empty blocks before sending).
 */
export const BeltBlockSchema = z.object({
  id: z.string(),
  label: z.string(),
  order: z.number().int(),
  rows: z.array(DashboardRowSchema),
});
export type BeltBlock = z.infer<typeof BeltBlockSchema>;

/**
 * Payload for `GET /api/surface` — the two parts of the Operations Surface
 * (ADR-0119): the rule-surfaced attention **belt** (grouped into ordered
 * `BeltBlock`s — build-plan Part 3) and the operator's private **pinned**
 * watches.
 */
export const SurfaceResponseSchema = z.object({
  belt: z.array(BeltBlockSchema),
  pinned: z.array(SurfacePinSchema),
});
export type SurfaceResponse = z.infer<typeof SurfaceResponseSchema>;
